import React from 'react';
import { FiCheck, FiAlertCircle } from 'react-icons/fi';
import styles from '../Auth.module.css';

/**
 * GoalsCheckboxGroup - Structured goal selection with checkboxes and custom text
 */
const GoalsCheckboxGroup = ({
  goalsData,
  selectedGoals = [],
  onGoalToggle,
  customGoalText,
  onCustomGoalTextChange,
  goalsText,
  onGoalsTextChange,
  error,
  disabled = false,
}) => {
  // Check if a goal type is selected
  const isGoalSelected = (goalId) => {
    return selectedGoals.some(g => g.type === goalId);
  };

  // Handle goal toggle
  const handleToggle = (goalId) => {
    if (isGoalSelected(goalId)) {
      // Remove the goal
      onGoalToggle(selectedGoals.filter(g => g.type !== goalId));
    } else {
      // Add the goal
      onGoalToggle([...selectedGoals, { type: goalId, customText: null }]);
    }
  };

  // Handle custom goal checkbox toggle
  const handleCustomToggle = () => {
    if (isGoalSelected('custom')) {
      onGoalToggle(selectedGoals.filter(g => g.type !== 'custom'));
      onCustomGoalTextChange?.('');
    } else {
      onGoalToggle([...selectedGoals, { type: 'custom', customText: customGoalText || '' }]);
    }
  };

  // Update custom goal text in the goals array
  const handleCustomTextChange = (text) => {
    onCustomGoalTextChange?.(text);
    // Update the custom goal in the array
    const updatedGoals = selectedGoals.map(g => 
      g.type === 'custom' ? { ...g, customText: text } : g
    );
    onGoalToggle(updatedGoals);
  };

  return (
    <div className={styles['au-goals-group']}>
      <div className={styles['au-goals-list']}>
        {goalsData?.goals?.map((goal) => (
          <label 
            key={goal.id} 
            className={`${styles['au-goals-item']} ${
              isGoalSelected(goal.id) ? styles['au-goals-item--selected'] : ''
            }`}
          >
            <div className={styles['au-goals-item__checkbox']}>
              <input
                type="checkbox"
                checked={isGoalSelected(goal.id)}
                onChange={() => handleToggle(goal.id)}
                disabled={disabled}
                className={styles['au-goals-item__input']}
              />
              <div className={styles['au-goals-item__checkmark']}>
                {isGoalSelected(goal.id) && <FiCheck size={14} />}
              </div>
            </div>
            <div className={styles['au-goals-item__content']}>
              <span className={styles['au-goals-item__label']}>{goal.label}</span>
              {goal.description && (
                <span className={styles['au-goals-item__description']}>{goal.description}</span>
              )}
            </div>
          </label>
        ))}

        {/* Custom goal option */}
        <label 
          className={`${styles['au-goals-item']} ${styles['au-goals-item--custom']} ${
            isGoalSelected('custom') ? styles['au-goals-item--selected'] : ''
          }`}
        >
          <div className={styles['au-goals-item__checkbox']}>
            <input
              type="checkbox"
              checked={isGoalSelected('custom')}
              onChange={handleCustomToggle}
              disabled={disabled}
              className={styles['au-goals-item__input']}
            />
            <div className={styles['au-goals-item__checkmark']}>
              {isGoalSelected('custom') && <FiCheck size={14} />}
            </div>
          </div>
          <div className={styles['au-goals-item__content']}>
            <span className={styles['au-goals-item__label']}>Other goal</span>
            <span className={styles['au-goals-item__description']}>Specify your own goal</span>
          </div>
        </label>

        {/* Custom goal text input */}
        {isGoalSelected('custom') && (
          <div className={styles['au-goals-custom-input']}>
            <input
              type="text"
              value={customGoalText || ''}
              onChange={(e) => handleCustomTextChange(e.target.value)}
              placeholder="Describe your goal..."
              className={styles['au-input']}
              disabled={disabled}
            />
          </div>
        )}
      </div>

      {/* Additional goals text */}
      <div className={styles['au-input-group']} style={{ marginTop: '1.25rem' }}>
        <label className={styles['au-input-group__label']}>
          Additional goals <span className={styles['au-input-group__optional']}>(Optional)</span>
        </label>
        <textarea
          value={goalsText || ''}
          onChange={(e) => onGoalsTextChange?.(e.target.value)}
          placeholder="Share any other goals or aspirations..."
          className={styles['au-textarea']}
          rows="3"
          disabled={disabled}
        />
      </div>

      {/* Error display */}
      {error && (
        <div className={styles['au-input-group__error']} style={{ marginTop: '0.5rem' }}>
          <FiAlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default GoalsCheckboxGroup;
